import { Effect, ParseResult, Schema } from "effect";
import { ConversationProduct } from "../models/shopper-ai/conversation/product";

/**
 * Non-empty literal tuple for `Schema.Literal(...)` from a string enum, as a raw
 * string union `.Type`. The `as` bridges `Object.values`' un-provable `string[]`.
 */
export const enumLiterals = <T extends Record<string, string>>(
  e: T,
): [`${T[keyof T]}`, ...`${T[keyof T]}`[]] =>
  Object.values(e) as [`${T[keyof T]}`, ...`${T[keyof T]}`[]];

// Helper to check if something is a Date-like object (handles cross-realm Date objects)
// The pg driver can return Date objects from different JS realms that fail instanceof checks
const isDateLike = (value: unknown): value is Date =>
  Object.prototype.toString.call(value) === "[object Date]";

// Accepts Date objects (from direct columns) or ISO strings (from json_build_object)
const isDateLikeOrString = (value: unknown): value is Date | string =>
  isDateLike(value) || typeof value === "string";

/**
 * Schema that handles Date objects from the pg driver which may be from different JS realms,
 * as well as ISO date strings from json_build_object subqueries.
 * Use this instead of Schema.DateFromSelf when reading dates from the database.
 */
export const DateFromDb = Schema.transform(
  Schema.declare(isDateLikeOrString, {
    identifier: "DateFromDb (Encoded side)",
    description:
      "Date object or ISO string from database (direct column or json_build_object)",
    jsonSchema: { type: "string", format: "date-time" },
  }),
  Schema.DateFromSelf,
  {
    strict: true,
    // Always re-create Date in current realm to handle cross-realm Date objects from pg driver
    decode: (value) =>
      typeof value === "string" ? new Date(value) : new Date(value.getTime()),
    encode: (value) => value,
  },
);

// Helper to check if something is null or a Date-like/string value
const isNullOrDateLikeOrString = (
  value: unknown,
): value is Date | string | null => value === null || isDateLikeOrString(value);

/**
 * Schema for nullable Date columns from the database.
 */
export const NullableDateFromDb = Schema.transform(
  Schema.declare(isNullOrDateLikeOrString, {
    identifier: "NullableDateFromDb (Encoded side)",
    description: "Nullable Date object or ISO string from database",
    jsonSchema: {
      oneOf: [{ type: "string", format: "date-time" }, { type: "null" }],
    },
  }),
  Schema.NullOr(Schema.DateFromSelf),
  {
    strict: true,
    // Always re-create Date in current realm to handle cross-realm Date objects from pg driver
    decode: (value) => {
      if (value === null) {
        return null;
      }
      return typeof value === "string"
        ? new Date(value)
        : new Date(value.getTime());
    },
    encode: (value) => value,
  },
);

// Helper to check if something is a Buffer-like object
const isBufferLike = (value: unknown): value is Buffer =>
  Buffer.isBuffer(value) ||
  (value instanceof Uint8Array &&
    Object.prototype.toString.call(value) === "[object Uint8Array]");

/**
 * Schema for BYTES columns in CockroachDB that store MongoDB ObjectIds as binary.
 * The pg driver returns these as Buffer objects.
 */
export const BufferFromDb = Schema.declare(isBufferLike, {
  identifier: "BufferFromDb",
  description:
    "Buffer from database (for BYTES columns storing MongoDB ObjectIds)",
});

/**
 * Schema for conversation_product[] columns in CockroachDB.
 * CockroachDB returns arrays as PostgreSQL string format "{value1,value2}".
 */
export const ConversationProductsFromDb = Schema.transformOrFail(
  Schema.Union(Schema.String, Schema.Null),
  Schema.Array(ConversationProduct),
  {
    strict: true,
    decode: (value, _, ast) => {
      if (value === null || value === "{}" || value === "") {
        return Effect.succeed([]);
      }
      const inner = value.slice(1, -1);
      const arr = inner.split(",");
      return Schema.decodeUnknown(Schema.Array(ConversationProduct))(arr).pipe(
        Effect.mapError((e) => new ParseResult.Type(ast, value, e.message)),
      );
    },
    encode: (value) => Effect.succeed(`{${value.join(",")}}`),
  },
);

// cspell:ignore pgvector
/**
 * Schema for CockroachDB VECTOR columns. The pg driver returns them in
 * pgvector text format: `[1.23,4.56,...]`. Decodes that to `readonly number[]`
 * and encodes a number array back to the same text form for parameter binding
 * via `${value}::VECTOR`.
 */
export const EmbeddingFromDb = Schema.transformOrFail(
  Schema.String,
  Schema.Array(Schema.Number),
  {
    strict: true,
    decode: (value, _, ast) => {
      if (
        value.length < 2 ||
        value.charCodeAt(0) !== 0x5b ||
        value.charCodeAt(value.length - 1) !== 0x5d
      ) {
        return Effect.fail(
          new ParseResult.Type(ast, value, "Expected `[...]` vector literal"),
        );
      }
      const inner = value.slice(1, -1);
      if (inner.length === 0) {
        return Effect.succeed([]);
      }
      const parts = inner.split(",");
      const out = new Array<number>(parts.length);
      for (const [i, part] of parts.entries()) {
        const n = Number(part);
        if (!Number.isFinite(n)) {
          return Effect.fail(
            new ParseResult.Type(
              ast,
              value,
              `Non-finite element at index ${i}`,
            ),
          );
        }
        out[i] = n;
      }
      return Effect.succeed(out);
    },
    encode: (value) => Effect.succeed(`[${value.join(",")}]`),
  },
);

/**
 * Coerces a value that may arrive as either a JSON string or a JSON number
 * into a string, normalizing numbers via `String(value)`. Useful for webhook
 * envelopes (e.g. TikTok/Postscript) that are inconsistent about whether
 * numeric identifiers like `shop_id` arrive quoted or as bare numbers, so a
 * numeric value doesn't reject the entire payload.
 */
export const StringFromStringOrNumber = Schema.transform(
  Schema.Union(Schema.String, Schema.Number),
  Schema.String,
  {
    strict: true,
    decode: (value) => (typeof value === "number" ? String(value) : value),
    encode: (value) => value,
  },
);

/**
 * Recursively converts ISO date strings to Date objects in a plain object.
 * Needed because JSON serialization converts Dates to strings, but z.date() expects Date instances.
 */
export function coerceDates(value: unknown): unknown {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }
  if (Array.isArray(value)) {
    return value.map(coerceDates);
  }
  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      result[k] = coerceDates(v);
    }
    return result;
  }
  return value;
}
